<script>
import { mapActions } from 'vuex';
import { message } from 'ant-design-vue';
import toggleModal from '@/mixins/toggleModal';
import UploadImage from '@/components/ux/upload/Upload-image.vue';
import uploadFile from '@/utils/uploadFile';
import apiCall from '@/store/apiCall';
import URLS from '@/store/urls';

export default {
  components: {
    UploadImage,
  },
  props: {
    company: {
      type: Object,
      default: null,
    },
    limit: {
      type: Number,
      default: 10,
    },
    offset: {
      type: Number,
      default: 0,
    },
  },
  mixins: [toggleModal],
  data() {
    return {
      loading: false,
      uploadingImage: false,
      cardImageFiles: [],
      cardImageMobileFiles: [],
      logoImageFiles: [],
      watermarkFiles: [],
      uploadingWatermark: false,
      activeTab: 'general',
      formData: this.company ? {
        displayName: this.company.displayName,
        description: this.company.description || '',
        pageKey: this.company.pageKey,
        seoTitle: this.company.seoTitle || '',
        seoDescription: this.company.seoDescription || '',
        seoKeywords: this.company.seoKeywords || '',
        cardTitle: this.company.cardTitle || '',
        cardTitleMobile: this.company.cardTitleMobile || '',
        cardDescription: this.company.cardDescription || '',
        cardDescriptionMobile: this.company.cardDescriptionMobile || '',
        cardImage: this.company.cardImage || '',
        cardImageMobile: this.company.cardImageMobile || '',
      } : {
        displayName: '',
        description: '',
        pageKey: '',
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
        cardTitle: '',
        cardTitleMobile: '',
        cardDescription: '',
        cardDescriptionMobile: '',
        cardImage: '',
        cardImageMobile: '',
      },
    };
  },
  computed: {
    isEditMode() {
      return this.company !== null;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Company' : 'Create New Company';
    },
    submitLabel() {
      return this.isEditMode ? 'Update Company' : 'Create Company';
    },
    logoImageInitial() {
      return this.formData.logoImageUri ? [this.formData.logoImageUri] : [];
    },
    cardImageInitial() {
      return this.formData.cardImage ? [this.formData.cardImage] : [];
    },
    cardImageMobileInitial() {
      return this.formData.cardImageMobile ? [this.formData.cardImageMobile] : [];
    },
    watermarkImageInitial() {
      return this.company?.watermarkImageUrl ? [this.company.watermarkImageUrl] : [];
    },
  },
  watch: {
    company: {
      handler(newVal) {
        if (newVal) {
          this.formData = {
            displayName: newVal.displayName,
            description: newVal.description || '',
            pageKey: newVal.pageKey,
            logoImageUri: newVal.logoImageUri || '',
            logoImageKey: newVal.logoImageKey || '',
            seoTitle: newVal.seoTitle || '',
            seoDescription: newVal.seoDescription || '',
            seoKeywords: newVal.seoKeywords || '',
            cardTitle: newVal.cardTitle || '',
            cardTitleMobile: newVal.cardTitleMobile || '',
            cardDescription: newVal.cardDescription || '',
            cardDescriptionMobile: newVal.cardDescriptionMobile || '',
            cardImage: newVal.cardImage || '',
            cardImageMobile: newVal.cardImageMobile || '',
          };
        } else {
          this.formData = {
            displayName: '',
            description: '',
            pageKey: '',
            logoImageUri: '',
            logoImageKey: '',
            seoTitle: '',
            seoDescription: '',
            seoKeywords: '',
            cardTitle: '',
            cardTitleMobile: '',
            cardDescription: '',
            cardDescriptionMobile: '',
            cardImage: '',
            cardImageMobile: '',
          };
        }
      },
      immediate: true,
    },
    show(val) {
      if (val) {
        this.activeTab = 'general';
      }
    },
  },
  methods: {
    ...mapActions({
      createCompanyAction: 'companies/createCompany',
      updateCompanyAction: 'companies/updateCompany',
    }),
    noSpacesRule({ value }) {
      return !value || !value.includes(' ');
    },
    handleCardImageUpdate(files) {
      this.cardImageFiles = files;
    },
    handleCardImageMobileUpdate(files) {
      this.cardImageMobileFiles = files;
    },
    handleLogoImageUpdate(files) {
      this.logoImageFiles = files;
    },
    handleWatermarkUpdate(files) {
      this.watermarkFiles = files;
    },
    async uploadWatermark() {
      if (!this.company?.id) return;
      const file = this.watermarkFiles[0];
      if (!file?.file) return;

      if (file.file.type !== 'image/png') {
        message.error('Only PNG files are allowed for watermarks (transparent background needed)');
        return;
      }

      this.uploadingWatermark = true;
      try {
        const formData = new FormData();
        formData.append('file', file.file);
        await apiCall({
          url: `${URLS.companies}/${this.company.id}/watermark`,
          type: 'PUT',
          body: formData,
          headers: { 'content-type': 'multipart/form-data' },
          isToken: true,
        });
        message.success('Watermark uploaded successfully');
      } catch (err) {
        message.error('Failed to upload watermark');
      } finally {
        this.uploadingWatermark = false;
      }
    },
    async removeWatermark() {
      if (!this.company?.id) return;
      this.uploadingWatermark = true;
      try {
        await apiCall({
          url: `${URLS.companies}/${this.company.id}/watermark`,
          type: 'DELETE',
          isToken: true,
        });
        message.success('Watermark removed');
      } catch (err) {
        message.error('Failed to remove watermark');
      } finally {
        this.uploadingWatermark = false;
      }
    },
    async uploadImages() {
      const uploadPromises = [];

      if (this.cardImageFiles.length > 0 && this.cardImageFiles[0].file) {
        uploadPromises.push(
          uploadFile(this.cardImageFiles[0].file, 'default')
            .then((response) => {
              this.formData.cardImage = response.data.data.url;
            })
            .catch((err) => {
              message.error('Failed to upload card image');
              throw err;
            }),
        );
      } else if (this.cardImageFiles.length > 0 && this.cardImageFiles[0].url) {
        this.formData.cardImage = this.cardImageFiles[0].url;
      }

      if (this.cardImageMobileFiles.length > 0 && this.cardImageMobileFiles[0].file) {
        uploadPromises.push(
          uploadFile(this.cardImageMobileFiles[0].file, 'default')
            .then((response) => {
              this.formData.cardImageMobile = response.data.data.url;
            })
            .catch((err) => {
              message.error('Failed to upload mobile card image');
              throw err;
            }),
        );
      } else if (this.cardImageMobileFiles.length > 0 && this.cardImageMobileFiles[0].url) {
        this.formData.cardImageMobile = this.cardImageMobileFiles[0].url;
      }

      if (this.logoImageFiles.length > 0 && this.logoImageFiles[0].file) {
        uploadPromises.push(
          uploadFile(this.logoImageFiles[0].file, 'default')
            .then((response) => {
              this.formData.logoImageUri = response.data.data.url;
              this.formData.logoImageKey = response.data.data.key;
            })
            .catch((err) => {
              message.error('Failed to upload logo image');
              throw err;
            }),
        );
      } else if (this.logoImageFiles.length > 0 && this.logoImageFiles[0].url) {
        this.formData.logoImageUri = this.logoImageFiles[0].url;
      }

      if (uploadPromises.length > 0) {
        this.uploadingImage = true;
        await Promise.all(uploadPromises);
        this.uploadingImage = false;
      }
    },
    async handleOk(values) {
      this.loading = true;
      try {
        await this.uploadImages();

        const finalValues = {
          ...values,
          pageKey: values.pageKey ? values.pageKey.trim().toLowerCase() : '',
          cardImage: this.formData.cardImage || '',
          cardImageMobile: this.formData.cardImageMobile || '',
          logoImageUri: this.formData.logoImageUri || '',
          logoImageKey: this.formData.logoImageKey || '',
        };

        if (this.isEditMode) {
          await this.updateCompanyAction({
            id: this.company.id,
            values: finalValues,
            limit: this.limit,
            offset: this.offset,
          });
        } else {
          await this.createCompanyAction({
            values: finalValues,
            limit: this.limit,
            offset: this.offset,
          });
        }
        this.hideModal();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <a-modal :open="show" :footer="null" @cancel="hideModal" width="900px">
      <a-spin :spinning="loading || uploadingImage">
        <FormKit
          type="form"
          :submit-label="submitLabel"
          @submit="handleOk"
          :value="formData"
          :key="company?.id || 'new'"
        >
          <h1 class="modal-heading">{{ modalTitle }}</h1>

          <a-tabs v-model:activeKey="activeTab" type="card" class="company-tabs">
            <!-- Tab 1: General -->
            <a-tab-pane key="general" tab="General">
              <div class="tab-content">
                <FormKit
                  type="text"
                  name="displayName"
                  label="Display Name"
                  placeholder="Enter display name"
                  validation="required"
                />

                <FormKit
                  type="text"
                  name="pageKey"
                  label="Page Key"
                  placeholder="Enter page key"
                  validation="required|noSpaces"
                  :validation-rules="{ noSpaces: noSpacesRule }"
                  :validation-messages="{ noSpaces: 'Spaces are not allowed in Page Key.' }"
                  help="Unique identifier for the company page (will be converted to lowercase)"
                />

                <FormKit
                  type="textarea"
                  name="description"
                  label="Description"
                  placeholder="Enter description (optional)"
                  :rows="4"
                />

                <div class="upload-field">
                  <div class="upload-label">Company Logo</div>
                  <UploadImage
                    :is-multiple="false"
                    :initial-images="logoImageInitial"
                    @update:files="handleLogoImageUpdate"
                  />
                </div>
              </div>
            </a-tab-pane>

            <!-- Tab 2: Card Settings -->
            <a-tab-pane key="card" tab="Card Settings">
              <div class="tab-content">
                <div class="card-columns">
                  <div class="card-column">
                    <h3 class="column-title">Desktop</h3>

                    <FormKit
                      type="text"
                      name="cardTitle"
                      label="Card Title"
                      placeholder="Enter card title (optional)"
                    />

                    <FormKit
                      type="textarea"
                      name="cardDescription"
                      label="Card Description"
                      placeholder="Enter card description (optional)"
                      :rows="3"
                    />

                    <div class="upload-field">
                      <div class="upload-label">Card Image</div>
                      <UploadImage
                        :is-multiple="false"
                        :initial-images="cardImageInitial"
                        @update:files="handleCardImageUpdate"
                      />
                    </div>
                  </div>

                  <div class="card-column">
                    <h3 class="column-title">Mobile</h3>

                    <FormKit
                      type="text"
                      name="cardTitleMobile"
                      label="Card Title (Mobile)"
                      placeholder="Enter mobile card title (optional)"
                    />

                    <FormKit
                      type="textarea"
                      name="cardDescriptionMobile"
                      label="Card Description (Mobile)"
                      placeholder="Enter mobile card description (optional)"
                      :rows="3"
                    />

                    <div class="upload-field">
                      <div class="upload-label">Card Image (Mobile)</div>
                      <UploadImage
                        :is-multiple="false"
                        :initial-images="cardImageMobileInitial"
                        @update:files="handleCardImageMobileUpdate"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </a-tab-pane>

            <!-- Tab 3: SEO -->
            <a-tab-pane key="seo" tab="SEO">
              <div class="tab-content">
                <FormKit
                  type="text"
                  name="seoTitle"
                  label="SEO Title"
                  placeholder="Enter SEO title (optional)"
                />

                <FormKit
                  type="textarea"
                  name="seoDescription"
                  label="SEO Description"
                  placeholder="Enter SEO description (optional)"
                  :rows="3"
                />

                <FormKit
                  type="text"
                  name="seoKeywords"
                  label="SEO Keywords"
                  placeholder="Enter SEO keywords (optional)"
                  help="Comma-separated keywords"
                />
              </div>
            </a-tab-pane>

            <!-- Tab 4: Watermark (edit mode only) -->
            <a-tab-pane v-if="isEditMode" key="watermark" tab="Watermark">
              <div class="tab-content">
                <a-spin :spinning="uploadingWatermark">
                  <p class="section-hint">
                    PNG only (transparent background). This watermark will be
                    applied to all new photo gallery uploads for this company.
                  </p>

                  <div class="upload-field">
                    <div class="upload-label">Watermark Image (PNG)</div>
                    <UploadImage
                      :is-multiple="false"
                      :initial-images="watermarkImageInitial"
                      @update:files="handleWatermarkUpdate"
                      accept="image/png"
                    />
                  </div>

                  <div class="watermark-actions">
                    <a-button
                      type="primary"
                      size="small"
                      :disabled="!watermarkFiles.length || !watermarkFiles[0]?.file"
                      @click="uploadWatermark"
                    >
                      Upload Watermark
                    </a-button>
                    <a-popconfirm
                      v-if="company?.watermarkImageUrl"
                      title="Remove watermark from this company?"
                      @confirm="removeWatermark"
                    >
                      <a-button size="small" danger>
                        Remove Watermark
                      </a-button>
                    </a-popconfirm>
                  </div>
                </a-spin>
              </div>
            </a-tab-pane>
          </a-tabs>
        </FormKit>
      </a-spin>
    </a-modal>
  </div>
</template>

<style scoped lang="scss">
.modal-heading {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.company-tabs {
  :deep(.ant-tabs-nav) {
    margin-bottom: 0;
  }

  :deep(.ant-tabs-content-holder) {
    border: 1px solid #e5e7eb;
    border-top: none;
    border-radius: 0 0 8px 8px;
    background: #fafbfc;
  }
}

.tab-content {
  padding: 1.5rem;
}

.card-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.card-column {
  padding: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.column-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #374151;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #3b82f6;
}

.upload-field {
  margin-top: 1rem;

  :deep(.image-uploader) {
    .ant-upload-select,
    .ant-upload-list-item,
    .ant-upload-list-item-container {
      width: 100% !important;
      max-width: 360px !important;
      height: 240px !important;
    }

    .ant-upload-select-picture-card {
      width: 100% !important;
      max-width: 360px !important;
      height: 240px !important;
    }

    .ant-upload-list-picture-card-container {
      width: 100% !important;
      max-width: 360px !important;
      height: 240px !important;
    }
  }
}

.upload-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.section-hint {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.watermark-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
</style>
